// inspired by https://github.com/CSFrequency/react-firebase-hooks/blob/master/auth/useSignInWithPopup.ts

import {logEvent} from "@/utils/analytics";
import {useMutation, UseMutationResult} from "@tanstack/react-query";
import {
  Auth,
  AuthError,
  AuthProvider,
  CustomParameters,
  FacebookAuthProvider,
  // GithubAuthProvider,
  GoogleAuthProvider,
  linkWithPopup,
  linkWithRedirect,
  // OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  // TwitterAuthProvider,
  UserCredential,
} from "firebase/auth";
import {useCallback} from "react";

export type AuthMode = "popup" | "redirect";

export type AuthType = "signIn" | "link";

export type Provider =
  // | "apple.com"
  | "facebook"
  // | "github"
  | "google";
// | "microsoft.com"
// | "twitter"
// | "yahoo.com";

export type AuthActionHook<M> = [M, boolean, AuthError | undefined];

export type Result = UseMutationResult<
  UserCredential | undefined,
  AuthError,
  {scopes?: string[]; customOAuthParameters?: CustomParameters}
>;

const getProvider = (id: Provider) => {
  switch (id) {
    case "facebook":
      return new FacebookAuthProvider();
    // case "github":
    //   return new GithubAuthProvider();
    case "google":
      return new GoogleAuthProvider();
    // case "twitter":
    //   return new TwitterAuthProvider();
    // default:
    //   return new OAuthProvider(id);
  }
};

export const useAuthWithProvider = (
  auth: Auth,
  type: AuthType,
  mode: AuthMode,
  providerId: Provider,
): Result => {
  const createAuthProvider = useCallback(
    (scopes?: string[], customOAuthParameters?: CustomParameters) => {
      // get provider instance
      const provider = getProvider(providerId);

      // add scopes to provider
      if (scopes) {
        scopes.forEach((scope) => provider.addScope(scope));
      }

      // add custom OAuth parameters to provider
      if (customOAuthParameters) {
        provider.setCustomParameters(customOAuthParameters);
      }

      return provider;
    },
    [providerId],
  );

  return useAuth(auth, type, mode, createAuthProvider);
};

const useAuth: (
  auth: Auth,
  type: AuthType,
  mode: AuthMode,
  createProvider: (
    scopes?: string[],
    customOAuthParameters?: CustomParameters,
  ) => AuthProvider,
) => Result = (auth, type, mode, createProvider) =>
  useMutation<
    UserCredential | undefined,
    AuthError,
    {scopes?: string[]; customOAuthParameters?: CustomParameters}
  >({
    mutationFn: ({scopes, customOAuthParameters}) => {
      // get provider instance with scopes and custom OAuth parameters
      const provider = createProvider(scopes, customOAuthParameters);
      const {providerId} = provider;

      const handleAuthSuccess = (result: UserCredential) => {
        logEvent("logged_in", {method: providerId});
        return result;
      };

      if (type === "signIn") {
        const signInMethod =
          mode === "popup"
            ? signInWithPopup(auth, provider)
            : signInWithRedirect(auth, provider);

        return signInMethod.then(handleAuthSuccess);
      }

      if (type === "link") {
        const linkMethod =
          mode === "popup"
            ? linkWithPopup(auth.currentUser!, provider)
            : linkWithRedirect(auth.currentUser!, provider);

        return linkMethod.then(handleAuthSuccess);
      }

      return Promise.resolve(undefined);
    },
  });
